import axios from "http/customAxios";
import { Reservation } from "../../modification/model";

export interface PaymentOrderDto {
  hotelTicker: string;
  reservationId: string;
  paymentTypeTicker?: string;
  country: string;
  language: string;
  uuid?: string;
  status: string;
  amount: number;
  currency: string;
  additional?: string;
  service?: string;
  paymentProvider: string;
  customerId: string;
  customerEmail: string;
  token?: string;
  paid: boolean;
  safe: boolean;
  refunded: number;
  ipAddress?: string;
  cardNumber?: string;
  cardUserToken?: string;
}

export interface PaymentTransactionDto {
  hotelTicker: string;
  paymentProvider: string;
  id?: string;
  reservationId: string;
  description?: string;
  uuid?: string;
  operativeType?: string;
  operationType?: string;
  operationTypeDescription?: string;
  amount: number;
  authorization?: number | string;
  status: string;
  service?: string;
  user: string;
  orderId: string;
  paymentMethodSelected?: string;
  paymentMessage?: string;
  cardUserId?: string;
  cardUserToken?: string;
  subscriptionPlan?: string;
  cardHolderName?: string;
  cardNumber?: string;
  cardType?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentsApiChargePayloadDto {
  reservationId: string;
  amount: number;
  description: string;
  email: string;
}

export interface PaymentsApiFreedomPayChargePayloadDto {
  reservation: Reservation;
  amount: number;
  description: string;
  email: string;
}

export interface PaymentsApiRefundPayloadDto {
  reservationId: string;
  amount: number;
  description: string;
  email: string;
  transactionId: string;
}

export class PaymentsApi {
  public readonly baseUrl: string;
  public readonly propertyTicker: string;

  constructor(baseUrl: string, propertyTicker: string) {
    this.baseUrl = baseUrl;
    this.propertyTicker = propertyTicker;
  }

  async addonPaymentsCharge(
    payload: PaymentsApiChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/addon-payments/${this.propertyTicker}/charges`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async paycometCharge(
    payload: PaymentsApiChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/paycomet/${this.propertyTicker}/charges`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async paycometRefund(
    payload: PaymentsApiRefundPayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/paycomet/${this.propertyTicker}/refund`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async getPaymentOrder(
    reservationId: string | number,
    authToken: string
  ): Promise<PaymentOrderDto> {
    const endpoint = `${this.baseUrl}/payment-orders/${this.propertyTicker}/reservations/${reservationId}`;
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: authToken,
      },
    });
    return response.data;
  }

  async listTransactions(
    reservationId: string | number,
    authToken: string
  ): Promise<{ pageSize: number; data: PaymentTransactionDto[] }> {
    const endpoint = `${this.baseUrl}/transactions/${this.propertyTicker}/reservations/${reservationId}`;
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: authToken,
      },
    });
    return response.data;
  }

  async adyenCharge(
    payload: PaymentsApiChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(`${this.baseUrl}/adyen/${this.propertyTicker}/charges`, payload, {
      headers: {
        Authorization: authToken,
        "x-payment-method": paymentMethod,
      },
    });
  }

  async stripeCharge(
    payload: PaymentsApiChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(`${this.baseUrl}/stripe/${this.propertyTicker}/charges`, payload, {
      headers: {
        Authorization: authToken,
        "x-payment-method": paymentMethod,
      },
    });
  }

  async payNoPainV3Charge(
    payload: PaymentsApiChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/paynopain/v3/${this.propertyTicker}/charges`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async payNoPainV3Refund(
    payload: PaymentsApiRefundPayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/paynopain/v3/${this.propertyTicker}/refund`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async stripeRefund(
    payload: PaymentsApiRefundPayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/stripe/${this.propertyTicker}/refund`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async adyenRefund(
    payload: PaymentsApiRefundPayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/adyen/${this.propertyTicker}/refund`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async freedomCharge(
    { amount, email, reservation, description }: PaymentsApiFreedomPayChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    try {
      await axios.post(
        `${this.baseUrl}/freedompay/${this.propertyTicker}/charges`,
        {
          amount,
          description,
          email,
          reservation: {
            hotelTicker: this.propertyTicker,
            reservationId: reservation.id,
            roomStays: reservation.roomReservations.map(it => ({
              startDate: it.checkIn,
              endDate: it.checkOut,
              totalRate: it.totalAmount,
            })),
          },
        },
        {
          headers: {
            Authorization: authToken,
            "x-payment-method": paymentMethod,
          },
        }
      );
    } catch (error) {
      // AWS Gateway timeout. FreedomPay create charge timeout is 35 seconds, but AWS Gateway timeout
      // is 29 seconds, so, we need to handle this case to not show an error to the user if the charge
      // is finally created correctly
      if (error.response && error.response.status === 504) {
        console.warn("AWS Gateway Timeout. Waiting for FreedomPay charge creation timeout...");

        // we will wait another 6-7s to retrieve the transactions and view if a transaction is saved
        await new Promise(resolve => setTimeout(resolve, 7000));
        const transactions = await this.listTransactions(reservation.id, authToken);

        const currentTransaction = transactions.data.find(
          t => t.amount === amount && t.description === description
        );
        if (!currentTransaction || currentTransaction.status !== "SUCCESS") {
          throw new Error("FreedomPay charge creation failed after AWS Gateway timeout");
        }
      }
    }
  }
}
